import React, { useContext, useState } from 'react';

import { FlexCol, FlexRow, TitleSmall } from '../../../common/components/v2';
import { DocumentType } from '../../../common/constants/documentType';
import { ReasonsTransform } from '../../../common/constants/reasons';
import { Resource } from '../../../common/constants/resource';
import { TaskState } from '../../../common/constants/states';
import ElementLocators from '../../../common/ElementLocators';
import { FeatureFlagsContext } from '../../../common/featureFlagsWrapper';
import { evidenceExists, getFormattedTimestamp } from '../../../common/helpers';
import { ITask } from '../../tasks/interfaces/ITask';
import { Eti } from '../../tasks/types/eti';
import styles from '../styles.module.scss';
import { Feedback } from './components/Feedback';
import { FileDetail } from './components/FileDetail';
import { FileUpload } from './components/FileUpload';
import { NoraTaskIcon } from './components/NoraTaskIcon';
import { Remarks } from './components/Remarks';
import { TaskDetailsItem } from './components/TaskDetailsItem';
import { TaskNote } from './components/TaskNote';
import { ETIComponent } from './etiGenerator/containers';
import { ETIFormData } from './etiGenerator/types';

export const ETIDetail = ({
  task,
  uploadHandler,
  downloadHandler,
  taskStateHandler,
  feedbackHandler,
  deleteHandler,
  upsertHandler,
  policyNumber,
}: {
  task: ITask;
  upsertHandler: (data: ETIFormData) => Promise<boolean>;
  uploadHandler: (
    existingTaskData: ITask,
    data: FormData,
    type: string,
  ) => void;
  downloadHandler: (uri: string, fileName: string) => void;
  deleteHandler: (
    existingTaskData: ITask,
    documentId: number,
    reason: string,
  ) => void;
  taskStateHandler: (taskId: number, state: TaskState, reason?: string) => void;
  feedbackHandler: (
    taskId: number,
    sufficent: boolean,
    feedback: string,
  ) => void;
  policyNumber: string;
}) => {
  const [needsFeedback, setneedsFeedback] = useState(false);

  const etiTask: Eti = task.taskData as Eti;
  const evidence = task.evidence;
  const evidenceReasons: string[] = etiTask.reason.map((item: string) => {
    return ReasonsTransform.getReasonDisplay(item);
  });

  const { flags } = useContext(FeatureFlagsContext);
  const { uwNativeEtiEnabled, uwWaiveRequestedEtiEnabled } = flags;

  const onFileSelection = (files: any, evidenceType: string) => {
    uploadHandler(task, files, evidenceType);
  };

  const onTaskStateChange = async (state: TaskState, reason?: string) => {
    if (state) {
      if (
        (needsFeedback === false && state === TaskState.COMPLETED) ||
        needsFeedback
      ) {
        setneedsFeedback(true);
        return;
      }
      taskStateHandler(task.id, state, reason);
    }
  };
  const onFeedback = async (
    sufficient: boolean,
    text: string,
    noOperation: boolean,
  ) => {
    if (noOperation) {
      setneedsFeedback(false);
      return;
    }
    setneedsFeedback(false);
    feedbackHandler(task.id, sufficient, text);
  };

  return (
    <div data-label={ElementLocators.LOCATOR_ETI_SECTION}>
      <FlexRow id={task.id.toString()} className={styles.Title}>
        <FlexCol className={styles.TitleTaskIcon}>
          <NoraTaskIcon
            disabled={
              uwNativeEtiEnabled &&
              task.state === TaskState.REQUESTED &&
              !uwWaiveRequestedEtiEnabled
            }
            task={task}
            onTaskStateClick={onTaskStateChange}
          />
        </FlexCol>
        <TitleSmall>{Resource.TASK_TITLE_ETI}</TitleSmall>
        <FlexCol className={styles.CreatedDate}>
          {getFormattedTimestamp(task.retriedAt ?? task.createdAt)}
        </FlexCol>
      </FlexRow>
      {task.state === TaskState.WAIVED && task.waiveReason && (
        <FlexRow className={styles.TaskSubtitle}>
          <strong>{Resource.WAIVE_REASON}:</strong>&nbsp;{task.waiveReason}
        </FlexRow>
      )}
      {needsFeedback ? (
        <Feedback show={needsFeedback} feedbackHandler={onFeedback}></Feedback>
      ) : task.sufficient !== null ? (
        <Remarks
          sufficient={task.sufficient}
          feedback={task.feedback}
        ></Remarks>
      ) : (
        <></>
      )}
      {(uwNativeEtiEnabled && task.state === TaskState.REQUESTED) ||
      etiTask.isNativeEti ? (
        <FlexRow>
          <ETIComponent
            policyNumber={policyNumber}
            taskId={task.id}
            onSubmit={upsertHandler}
            isExisting
          />
        </FlexRow>
      ) : (
        <TaskDetailsItem
          leftItem={Resource.QUESTIONS}
          rightItem={etiTask ? etiTask.question : Resource.NA}
        />
      )}
      {evidenceExists(evidence) && evidence && !etiTask.isNativeEti && (
        <FileDetail
          documents={evidence.documents}
          fileOpenHandler={downloadHandler}
          fileDeletehandler={(documentId: number, reason: string) =>
            deleteHandler(task, documentId, reason)
          }
        />
      )}
      {task.state !== TaskState.COMPLETED &&
        task.state !== TaskState.REQUESTED &&
        !etiTask.isNativeEti && (
          <FileUpload
            leftItem={evidenceExists(evidence) ? '' : Resource.FILES}
            selectedFiles={onFileSelection}
            type={DocumentType.ETI}
            ongoingUpload={task.ongoingUpload?.[DocumentType.ETI]}
          />
        )}
      {task.state !== TaskState.REQUESTED && (
        <TaskNote taskID={task.id} originalNote={etiTask?.note} />
      )}
      {(evidenceReasons?.length > 0 || etiTask?.reasonDescription) && (
        <>
          <FlexRow className={styles.TaskSubtitle}>{Resource.REASON}</FlexRow>
          {evidenceReasons?.length > 0 && (
            <TaskDetailsItem
              leftItem={Resource.EVIDENCE}
              rightItem={evidenceReasons.join(Resource.NORA_SEPARATOR)}
            />
          )}
          {etiTask?.reasonDescription && (
            <TaskDetailsItem
              leftItem={Resource.DESCRIPTION}
              rightItem={etiTask.reasonDescription}
            />
          )}
        </>
      )}
    </div>
  );
};
